import { XMarkIcon } from "@heroicons/react/24/solid";
import { Add, Edit } from "iconsax-react";
import { useState, useEffect } from "react";
import Button from "./Button";
import axios from "axios";
import { CREATE_ROLE_API, UPDATE_ROLE_API, GET_PERMISSION_LIST_API } from "../Constant/api";
import { useToast } from "./Toast";

const RoleModal = ({ toggleModal, onCreateRole, roleData = null }) => {
  const isEditMode = !!roleData;
  const { toast } = useToast();
  
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Fetch available permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await axios.get(GET_PERMISSION_LIST_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        });
        setAvailablePermissions(res.data.data || []);
      } catch (err) {
        console.error(err);
        toast("Failed to fetch permissions.", "error");
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [toast]);

  // Populate form if editing
  useEffect(() => {
    if (roleData) {
      setRoleName(roleData.name || "");
      setSelectedPermissions(roleData.permissions?.map(p => p.id) || []);
    }
  }, [roleData]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      toast("Please enter role name", "error");
      return;
    }

    if (selectedPermissions.length === 0) {
      toast("Please select at least one permission", "error");
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        name: roleName,
        permissions: selectedPermissions,
      };

      if (isEditMode) {
        await axios.put(`${UPDATE_ROLE_API}/${roleData.id}`, payload, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        });
        toast("Role updated successfully", "success");
      } else {
        await axios.post(CREATE_ROLE_API, payload, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        });
        toast("Role created successfully", "success");
      }

      onCreateRole();
      toggleModal();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 
        `Failed to ${isEditMode ? 'update' : 'create'} role`;
      toast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed flex left-0 top-0 z-20 w-full h-screen bg-black bg-opacity-50 backdrop-blur-md items-center justify-center">
      <div className="flex flex-col items-center w-[500px] max-h-[90vh] bg-white rounded-lg overflow-hidden">
        <div className="relative flex flex-col w-full p-[24px] gap-4 overflow-y-auto">
          <XMarkIcon
            className="absolute top-[24px] right-[24px] w-[24px] h-[24px] cursor-pointer"
            onClick={toggleModal}
          />

          {/* Icon */}
          <div className="flex size-[48px] items-center justify-center rounded-full bg-[#5C4BF5] bg-opacity-20 mt-[16px] self-center">
            {isEditMode ? (
              <Edit size={24} color="#5C4BF5" variant="Bold" />
            ) : (
              <Add size={24} color="#5C4BF5" variant="Bold" />
            )}
          </div>

          {/* Header */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="font-semibold text-[20px]">
              {isEditMode ? "Edit Role" : "Create New Role"}
            </h2>
            <p className="font-normal text-[14px] text-gray-600">
              {isEditMode 
                ? "Update the role details below"
                : "Fill in the details below to create a new role"
              }
            </p>
          </div>

          {/* Form */}
          <div className="flex flex-col gap-4 mt-4">
            {/* Role Name Input */}
            <div className="flex flex-col gap-2">
              <label className="text-[14px] font-medium text-gray-700">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Enter role name"
                className="px-[12px] py-[8px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C4BF5] focus:border-transparent"
                disabled={loading}
              />
            </div>

            {/* Permissions */}
            <div className="flex flex-col gap-2">
              <label className="text-[14px] font-medium text-gray-700">
                Permissions <span className="text-red-500">*</span>
              </label>
              
              {loadingPermissions ? (
                <div className="text-[14px] text-gray-500 py-4 text-center">
                  Loading permissions...
                </div>
              ) : availablePermissions.length === 0 ? (
                <div className="text-[14px] text-gray-500 py-4 text-center">
                  No permissions available
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg p-[12px] max-h-[300px] overflow-y-auto">
                  <div className="flex flex-col gap-2">
                    {availablePermissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="w-4 h-4 text-[#5C4BF5] border-gray-300 rounded focus:ring-[#5C4BF5]"
                          disabled={loading}
                        />
                        <span className="text-[14px] text-gray-700">
                          {permission.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-[12px] text-gray-500">
                Selected: {selectedPermissions.length} permission(s)
              </p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center w-full p-[24px] gap-2 border-t border-gray-200">
          <Button 
            variant="secondary" 
            className="w-full" 
            onClick={toggleModal}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            className="w-full" 
            onClick={handleSubmit}
            disabled={loading || loadingPermissions}
          >
            {loading ? "Saving..." : (isEditMode ? "Update" : "Create")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleModal;